import { useCallback, useState, type ReactNode } from "react";

import { UnsavedChangesDialog } from "@/components/UnsavedChangesDialog";

interface UseUnsavedChangesGuardArgs {
  /** Whether the form has unsaved input (e.g. react-hook-form `formState.isDirty`). */
  isDirty: boolean;
  /** The dialog's real open/close handler (sets the controlled `open` state). */
  onOpenChange: (open: boolean) => void;
}

export interface UnsavedChangesGuard {
  /**
   * Drop-in replacement for a Radix `<Dialog onOpenChange>`. Escape, click-outside,
   * and the X button all route through here as `onOpenChange(false)`; when the form
   * is dirty we show the confirmation instead of closing.
   */
  guardedOnOpenChange: (open: boolean) => void;
  /** For explicit Cancel buttons — same dirty check as a dialog dismissal. */
  requestClose: () => void;
  /** Render alongside the guarded dialog — the "Discard unsaved changes?" confirmation. */
  confirmation: ReactNode;
}

/**
 * Guards a dialog/form against accidental dismissal that would lose in-progress input.
 *
 * When the form is dirty, any close request (Escape, click-outside, X, or an explicit
 * Cancel button) opens a confirmation instead of closing. When the form is clean it
 * closes immediately, so untouched forms never nag the user. The hook owns the
 * confirmation UI; consumers wire {@link guardedOnOpenChange}/{@link requestClose}
 * and render {@link confirmation}.
 */
export function useUnsavedChangesGuard({
  isDirty,
  onOpenChange,
}: UseUnsavedChangesGuardArgs): UnsavedChangesGuard {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const requestClose = useCallback(() => {
    if (isDirty) {
      setConfirmOpen(true);
    } else {
      onOpenChange(false);
    }
  }, [isDirty, onOpenChange]);

  const guardedOnOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        onOpenChange(true);
        return;
      }
      requestClose();
    },
    [onOpenChange, requestClose]
  );

  const confirmDiscard = useCallback(() => {
    // Close the confirmation first, then the parent dialog. The global
    // usePointerEventsGuard (App.tsx) clears any stranded body pointer-events
    // from the two layers unwinding together (GLE-80).
    setConfirmOpen(false);
    onOpenChange(false);
  }, [onOpenChange]);

  const confirmation = (
    <UnsavedChangesDialog open={confirmOpen} onOpenChange={setConfirmOpen} onDiscard={confirmDiscard} />
  );

  return { guardedOnOpenChange, requestClose, confirmation };
}
